import type { PayloadAction } from '@reduxjs/toolkit';
import type { EditableField } from '@/types/editableField';
import { AssignmentAssigneeListItem, AssignmentQueueListItem } from '@/types/entities/assignmentQueueItem';
import type { CauseOfIncidentListItem } from '@/types/entities/causeOfIncidentListItem';
import type { UserSelectListItem } from '@/types/entities/userSelectListItem';
import type { PaginatedListResponse } from '@/types/pagination';
import type { StateSelectOption } from '@/types/stateSelectOption';
import type { GetClaimSetClaimUpdateOptionsResponse } from '@/features/editMode/types/getClaimSetClaimUpdateOptions';
import type { GetClaimSetUpdateOptionsResponse } from '@/features/editMode/types/getClaimSetUpdateOptions';
import type { SelectOption } from '@/features/editMode/types/selectOption';
import type { EdsValidationResponse } from '@/features/eds/types/api/responses/EdsValidationResponse';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import type { RootState } from '@/store/types/RootState';
import type { ContactPreferenceUpdateOptions } from '../types/contactPreferenceUpdateOptions';
import type { GetClaimSetTaskUpdateOptionsResponse } from '../types/getClaimSetTaskUpdateOptions';
import type { GetPolicyUpdateOptionsResponse } from '../types/getPolicyUpdateOptions';
import type { GetProfileUpdateOptionsResponse } from '../types/getProfileUpdateOptionsResponse';
import React from 'react';
import { call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { isAxiosError } from 'axios';
import { EditModeState } from '@/features/editMode/enums/EditModeState';
import { EditModeStatus } from '@/features/editMode/enums/EditModeStatus';
import EditModeEvents from '@/features/editMode/events';
import {
  selectAllAssignees,
  selectAssignmentListPagination,
  selectCauseOfIncidentListPagination,
  selectReportingUserListPagination,
  selectShouldCloseEditModeOnError,
  selectStateListPagination,
} from '@/features/editMode/store/editModeSelectors';
import {
  EditModeAssignee,
  addEditedField,
  changeEditModeState,
  changeEditModeStatus,
  closeEditMode,
  getAssigneeById,
  getAssigneeByIdSuccess,
  getAssignmentListData,
  getAssignmentListDataError,
  getAssignmentListDataSuccess,
  getBeneficiaryContactPreferenceUpdateOptions,
  getBeneficiaryContactPreferenceUpdateOptionsSuccess,
  getCauseOfIncidentListData,
  getCauseOfIncidentListDataError,
  getCauseOfIncidentListDataSuccess,
  getClaimSetClaimUpdateOptions,
  getClaimSetClaimUpdateOptionsSuccess,
  getClaimSetTaskUpdateOptions,
  getClaimSetTaskUpdateOptionsSuccess,
  getClaimSetUpdateOptions,
  getClaimSetUpdateOptionsSuccess,
  getClaimantUpdateOptions,
  getClaimantUpdateOptionsSuccess,
  getDocumentDeliveryPreferencesUpdateOptions,
  getDocumentDeliveryPreferencesUpdateOptionsSuccess,
  getGenderUpdateOptions,
  getGenderUpdateOptionsSuccess,
  getPolicyUpdateOptions,
  getPolicyUpdateOptionsSuccess,
  getProfileUpdateOptions,
  getProfileUpdateOptionsSuccess,
  getReportingUserListData,
  getReportingUserListDataError,
  getReportingUserListDataSuccess,
  getStateListData,
  getStateListDataError,
  getStateListDataSuccess,
  incrementReportingUserPage,
  removeHiddenEditedFields,
  resetEditedField,
  saveEditModeData,
  setAssigneesLoading,
  setAssignmentListSearchExpression,
  setCauseOfIncidentSearchExpression,
  setReportingUserSearchExpression,
  setStateListSearchExpression,
  startLoadingAssignmentListData,
  startLoadingCauseOfIncidentListData,
  startLoadingReportingUserListData,
  startLoadingStateListData,
  validateFieldsError,
  validateFieldsSuccess,
} from '@/features/editMode/store/editModeSlice';
import { handleEditModeLifecycleTelemetry } from '@/features/editMode/utils/handleEditModeLifecycleTelemetry';
import EdsCategoryEvents from '@/features/eds/components/EdsCategories/events';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { EdsStorageService } from '@/features/eds/services/EdsStorageService/EdsStorageService';
import { EdsValidationService } from '@/features/eds/services/EdsValidationService/EdsValidationSevice';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { selectCurrentClaimSetId } from '@/domains/claimSets/store/claimSetSlice';
import { selectUser } from '@/domains/user/store/selectors';
import { DynamicIcon } from '@/components/ui/icons/DynamicIcon/DynamicIcon';
import { CacheVariables } from '@/enums/CacheVariables';
import CacheService from '@/services/CacheService/CacheService';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import PaginationService from '@/services/PaginationService/PaginationService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import ServerEventService from '@/services/ServerEventService/ServerEventService';
import SnacksService from '@/services/SnackService/SnackService';

const EDIT_MODE_CACHE_PREFIX = 'EditMode';

function* getReportingUserListSaga({ type, payload }: PayloadAction<undefined | string>) {
  const shouldResetCache = [incrementReportingUserPage.type as string].includes(type);

  yield delay(300);

  try {
    let data = CacheService.get<PaginatedListResponse<UserSelectListItem>>(
      CacheVariables.ReportingUsers,
      shouldResetCache,
      setReportingUserSearchExpression.type ? payload : 1,
    );

    if (!data) {
      yield put(startLoadingReportingUserListData());
      const params = yield select(selectReportingUserListPagination);
      const response: HttpServiceResponse<PaginatedListResponse<UserSelectListItem>> = yield call(
        HttpService.get,
        `examiner/user/examiners?${QueryParamsService.constructSearchParamsFromRecord(params)}`,
      );

      CacheService.set<PaginatedListResponse<UserSelectListItem>>(
        CacheVariables.ReportingUsers,
        response.data,
        CacheService.minutes(1),
        setReportingUserSearchExpression.type ? payload : 1,
      );
      data = response.data;
    }

    if (data) {
      const normalizedData = PaginationService.normalizePaginatedListResponse(data);

      const currentUser = yield select(selectUser);
      const currentUserInList = normalizedData.dataList.find(
        (reportingUser) => reportingUser.username === currentUser?.username,
      );

      if (currentUserInList) {
        currentUserInList.firstName = 'Me';
        currentUserInList.lastName = '';
      }

      yield put(getReportingUserListDataSuccess(normalizedData));
    }
  } catch (error) {
    yield put(getReportingUserListDataError());
    console.error(error);
  }
}

function* getStateListSaga() {
  try {
    const params = yield select(selectStateListPagination);

    const cacheKey = [
      EDIT_MODE_CACHE_PREFIX,
      `examiner/Geography/new-states?${QueryParamsService.constructSearchParamsFromRecord(params)}`,
    ].join('_');
    let data = CacheService.get<StateSelectOption[]>(cacheKey, false, 1);

    if (!data) {
      yield put(startLoadingStateListData());
      const response: HttpServiceResponse<StateSelectOption[]> = yield call(
        HttpService.get,
        `examiner/Geography/new-states?${QueryParamsService.constructSearchParamsFromRecord(params)}`,
      );

      CacheService.set<StateSelectOption[]>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getStateListDataSuccess(data));
  } catch (error) {
    yield put(getStateListDataError());
    console.error(error);
  }
}

function* getClaimSetUpdateOptionsSaga() {
  try {
    const cacheKey = [EDIT_MODE_CACHE_PREFIX, 'examiner/update-options/claim'].join('_');
    let data = CacheService.get<GetClaimSetUpdateOptionsResponse>(cacheKey, false, 1);

    if (!data) {
      const response: HttpServiceResponse<GetClaimSetUpdateOptionsResponse> = yield call(
        HttpService.get,
        `examiner/update-options/claim-set`,
      );

      CacheService.set<GetClaimSetUpdateOptionsResponse>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getClaimSetUpdateOptionsSuccess(data));
  } catch (error) {
    console.error(error);
  }
}

function* getClaimSetClaimUpdateOptionsSaga() {
  try {
    const cacheKey = [EDIT_MODE_CACHE_PREFIX, 'examiner/update-options/claim'].join('_');
    let data = CacheService.get<GetClaimSetClaimUpdateOptionsResponse>(cacheKey, false, 1);

    if (!data) {
      const response: HttpServiceResponse<GetClaimSetClaimUpdateOptionsResponse> = yield call(
        HttpService.get,
        `examiner/update-options/claim`,
      );

      CacheService.set<GetClaimSetClaimUpdateOptionsResponse>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getClaimSetClaimUpdateOptionsSuccess(data));
  } catch (error) {
    console.error(error);
  }
}

function* getClaimSetTaskUpdateOptionsSaga() {
  try {
    const cacheKey = [EDIT_MODE_CACHE_PREFIX, 'examiner/update-options/task'].join('_');
    let data = CacheService.get<GetClaimSetTaskUpdateOptionsResponse>(cacheKey, false, 1);

    if (!data) {
      const response: HttpServiceResponse<GetClaimSetTaskUpdateOptionsResponse> = yield call(
        HttpService.get,
        `examiner/update-options/task`,
      );

      CacheService.set<GetClaimSetTaskUpdateOptionsResponse>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getClaimSetTaskUpdateOptionsSuccess(data));
  } catch (error) {
    console.error(error);
  }
}

function* getClaimantUpdateOptionsSaga() {
  try {
    const cacheKey = [EDIT_MODE_CACHE_PREFIX, 'examiner/update-options/claimant'].join('_');
    let data = CacheService.get<GetClaimSetTaskUpdateOptionsResponse>(cacheKey, false, 1);

    if (!data) {
      const response: HttpServiceResponse<GetClaimSetTaskUpdateOptionsResponse> = yield call(
        HttpService.get,
        `examiner/update-options/claimant`,
      );

      CacheService.set<GetClaimSetTaskUpdateOptionsResponse>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getClaimantUpdateOptionsSuccess(data));
  } catch (error) {
    console.error(error);
  }
}

function* getBeneficiaryContactPreferenceUpdateOptionsSaga() {
  try {
    const cacheKey = [EDIT_MODE_CACHE_PREFIX, 'examiner/update-options/contact-preference-options'].join('_');
    let data = CacheService.get<ContactPreferenceUpdateOptions>(cacheKey, false, 1);

    if (!data) {
      const response: HttpServiceResponse<ContactPreferenceUpdateOptions> = yield call(
        HttpService.get,
        `examiner/update-options/contact-preference-options`,
      );

      CacheService.set<ContactPreferenceUpdateOptions>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getBeneficiaryContactPreferenceUpdateOptionsSuccess(data));
  } catch (error) {
    console.error(error);
  }
}

function* getDocumentDeliveryPreferencesUpdateOptionsSaga() {
  try {
    const cacheKey = [EDIT_MODE_CACHE_PREFIX, 'examiner/update-options/document-delivery-preference-options'].join('_');
    let data = CacheService.get<SelectOption[]>(cacheKey, false, 1);

    if (!data) {
      const response: HttpServiceResponse<SelectOption[]> = yield call(
        HttpService.get,
        `examiner/update-options/document-delivery-preference-options`,
      );

      CacheService.set<SelectOption[]>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getDocumentDeliveryPreferencesUpdateOptionsSuccess(data));
  } catch (error) {
    console.error(error);
  }
}

function* getGenderUpdateOptionsSaga() {
  try {
    const cacheKey = [EDIT_MODE_CACHE_PREFIX, 'examiner/update-options/gender-options'].join('_');
    let data = CacheService.get<SelectOption[]>(cacheKey, false, 1);

    if (!data) {
      const response: HttpServiceResponse<SelectOption[]> = yield call(
        HttpService.get,
        `examiner/update-options/gender-options`,
      );

      CacheService.set<SelectOption[]>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getGenderUpdateOptionsSuccess(data));
  } catch (error) {
    console.error(error);
  }
}

function* getPolicyUpdateOptionsSaga() {
  try {
    const cacheKey = [EDIT_MODE_CACHE_PREFIX, 'examiner/update-options/policies-options'].join('_');
    let data = CacheService.get<GetPolicyUpdateOptionsResponse>(cacheKey, false, 1);

    if (!data) {
      const response: HttpServiceResponse<GetPolicyUpdateOptionsResponse> = yield call(
        HttpService.get,
        `examiner/update-options/policies-options`,
      );

      CacheService.set<GetPolicyUpdateOptionsResponse>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getPolicyUpdateOptionsSuccess(data));
  } catch (error) {
    console.error(error);
  }
}

function* getProfileUpdateOptionsSaga() {
  try {
    const cacheKey = [EDIT_MODE_CACHE_PREFIX, 'examiner/update-options/profile-options'].join('_');
    let data = CacheService.get<GetProfileUpdateOptionsResponse>(cacheKey, false, 1);

    if (!data) {
      const response: HttpServiceResponse<GetProfileUpdateOptionsResponse> = yield call(
        HttpService.get,
        `examiner/update-options/profile-options`,
      );

      CacheService.set<GetProfileUpdateOptionsResponse>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getProfileUpdateOptionsSuccess(data));
  } catch (error) {
    console.error(error);
  }
}

function* getCauseOfIncidentOptionsSaga() {
  yield delay(300);
  try {
    const params = yield select(selectCauseOfIncidentListPagination);

    const cacheKey = [
      EDIT_MODE_CACHE_PREFIX,
      `examiner/claims/search-icd-codes?${QueryParamsService.constructSearchParamsFromRecord(params)}`,
    ].join('_');
    let data = CacheService.get<CauseOfIncidentListItem[]>(cacheKey, false, 1);

    if (!data) {
      yield put(startLoadingCauseOfIncidentListData());

      const response: HttpServiceResponse<CauseOfIncidentListItem[]> = yield call(
        HttpService.get,
        `examiner/claims/search-icd-codes?${QueryParamsService.constructSearchParamsFromRecord(params)}`,
      );

      CacheService.set<CauseOfIncidentListItem[]>(cacheKey, response.data, CacheService.minutes(10), 1);
      data = response.data;
    }

    yield put(getCauseOfIncidentListDataSuccess(data));
  } catch (error) {
    yield put(getCauseOfIncidentListDataError());
    console.error(error);
  }
}

function* saveEditModeDataSaga() {
  const shouldCloseEditModeOnError = yield select(selectShouldCloseEditModeOnError);
  const editedFields: EditableField[] = yield select((state: RootState) => state.editMode.editedFields);

  try {
    yield put(setSystemLoadingStatus(true));

    const changes = editedFields.reduce<typeof editedFields>((acc, field) => {
      if (field.value === undefined) return acc;

      return [
        ...acc,
        {
          entityId: field.entityId,
          entityName: field.entityName,
          entityField: edsToEntityFormat(field.entityField),
          oldValue: field.oldValue?.toString(),
          value: field.value === null ? null : field.value?.toString(),
        },
      ];
    }, []);

    yield call(
      HttpService.post,
      `v2/update`,
      { changes },
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );
    yield put(closeEditMode({ newState: EditModeState.Saved }));

    ModalService.close(Modals.Confirmation);

    changes.forEach((change) => {
      EdsStorageService.removeDefinition(change.entityName);
      EdsStorageService.removeRawDefinition(change.entityName);
    });

    EventService.dispatch(new EditModeEvents.Save(editedFields));

    SnacksService.add({
      type: SnackType.Success,
      title: 'Updates saved successfully!',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
      slots: {
        left: React.createElement(DynamicIcon, { code: 'SUCCESS_ICON' }),
      },
    });

    yield put(setSystemLoadingStatus(false));
  } catch (error) {
    if (shouldCloseEditModeOnError) yield put(closeEditMode({ newState: EditModeState.Canceled }));
    yield put(setSystemLoadingStatus(false));

    const hasPasswordField = editedFields.some((field) => field.entityField.endsWith('Password'));
    let contentText = 'Please try again';

    if (isAxiosError(error) && hasPasswordField) {
      const responseData = error.response?.data;
      const detail = typeof responseData === 'string' ? responseData : responseData.detail;
      contentText = detail?.split(':').at(-1) || contentText;
    }

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: contentText,
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    console.error(error);
  } finally {
    handleEditModeLifecycleTelemetry();
    EventService.dispatch(new EditModeEvents.Close({}));
  }
}

function* closeEditModeSaga({
  payload: { newState, newStatus, shouldCloseAllConnections },
}: PayloadAction<{ newState: EditModeState; newStatus?: EditModeStatus; shouldCloseAllConnections?: boolean }>) {
  const claimSetId = yield select(selectCurrentClaimSetId);

  if (shouldCloseAllConnections) {
    ServerEventService.disconnectAll();
  } else if (claimSetId) {
    ServerEventService.disconnectConnection(`${import.meta.env.VITE_SSE_EDIT_MODE_HUB_URL}?claimSetId=${claimSetId}`);
  }

  EventService.dispatch(new EditModeEvents.Close({}));

  if (newStatus) yield put(changeEditModeStatus(newStatus));
  yield put(changeEditModeState(newState));
}

function* validateFieldsSaga() {
  try {
    const editedFields: EditableField[] = yield select((state: RootState) => state.editMode.editedFields);

    const changes = editedFields.map((field) => ({
      entityId: field.entityId,
      entityName: field.entityName,
      entityField: edsToEntityFormat(field.entityField),
      oldValue: field.oldValue?.toString(),
      value: field.value?.toString() || null,
    }));

    const { data }: HttpServiceResponse<EdsValidationResponse> = yield call(HttpService.post, `rules/on-edit`, {
      changes,
    });

    EdsValidationService.dispatchSetValues(data?.setValueResults || []);

    EdsValidationService.dispatchOverrides(data?.overrideResults || []);

    const normalizedValidationResultList = EdsValidationService.normalizeValidationResponse(data);

    yield put(validateFieldsSuccess(normalizedValidationResultList));

    yield put(
      removeHiddenEditedFields(
        EdsValidationService.prepareOverrideFields(data?.overrideResults || [], {
          filtering: {
            settingKey: 'hidden',
            value: true,
          },
        }),
      ),
    );
  } catch (error) {
    console.error(error);
    yield put(validateFieldsError());
  }
}

function* changeEditModeStateSaga() {
  const isInEditMode = yield select((state) => state.editMode.isInEditMode);

  if (isInEditMode) {
    EventService.dispatch(new EditModeEvents.Open({}));
    EventService.dispatch(new EdsCategoryEvents.OpenAll({}));
    yield delay(500);
    EventService.dispatch(new EdsCategoryEvents.OpenRest({}));
  } else EventService.dispatch(new EdsCategoryEvents.CloseAll({}));
}

function* getPendingAssigneesSaga() {
  yield delay(10);

  const assignees = yield select(selectAllAssignees);
  const pendingAssigneeIds = Object.entries<EditModeAssignee>(assignees)
    .filter(([_, assigneeData]) => assigneeData.status === 'pending')
    .map(([assigneeId, _]) => Number(assigneeId));

  if (!pendingAssigneeIds.length) {
    return;
  }

  yield put(setAssigneesLoading(pendingAssigneeIds));

  try {
    const requestUrl = `/filter/${EdsEntityCodes.Account}?includes=${EdsEntityCodes.Person}`;

    const { data } = yield call(
      HttpService.post,
      requestUrl,
      [
        {
          propertyName: EdsFilterEntities.PersonId,
          values: pendingAssigneeIds,
        },
      ],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse = yield call(EdsAdapterService.normalize, data.listings);

    yield put(getAssigneeByIdSuccess(normalizedResponse));
  } catch (error) {
    console.error(error);
  }
}

function* getAssignmentListSaga({ payload }: PayloadAction<{ id: string | number }>) {
  try {
    yield put(startLoadingAssignmentListData());

    const { searchExpression } = yield select(selectAssignmentListPagination);

    const queueId = payload?.id;
    const url = queueId
      ? `/examiner/user/assignee?searchExpression=${searchExpression}&queueId=${queueId}`
      : `/examiner/user/assignee?searchExpression=${searchExpression}`;

    const filters = [
      {
        propertyName: 'Disabled',
        values: ['false'],
      },
    ];

    const { data }: HttpServiceResponse<AssignmentQueueListItem[] | AssignmentAssigneeListItem[]> = yield call(
      HttpService.get,
      url,
      filters,
      {
        baseURL: import.meta.env.VITE_API_URL_V2,
      },
    );

    yield put(
      getAssignmentListDataSuccess({
        queueId: queueId != null ? Number(queueId) : undefined,
        data,
      }),
    );
  } catch (error) {
    yield put(getAssignmentListDataError());
    console.error(error);
  }
}

export function* editModeWatcher() {
  yield takeLatest(getClaimSetUpdateOptions.type, getClaimSetUpdateOptionsSaga);
  yield takeLatest(getClaimSetClaimUpdateOptions.type, getClaimSetClaimUpdateOptionsSaga);
  yield takeLatest(getClaimSetTaskUpdateOptions.type, getClaimSetTaskUpdateOptionsSaga);
  yield takeLatest(getClaimantUpdateOptions.type, getClaimantUpdateOptionsSaga);
  yield takeLatest(getDocumentDeliveryPreferencesUpdateOptions.type, getDocumentDeliveryPreferencesUpdateOptionsSaga);
  yield takeLatest(getGenderUpdateOptions.type, getGenderUpdateOptionsSaga);
  yield takeLatest(getPolicyUpdateOptions.type, getPolicyUpdateOptionsSaga);
  yield takeLatest(getProfileUpdateOptions.type, getProfileUpdateOptionsSaga);
  yield takeLatest(getBeneficiaryContactPreferenceUpdateOptions.type, getBeneficiaryContactPreferenceUpdateOptionsSaga);
  yield takeLatest(closeEditMode.type, closeEditModeSaga);
  yield takeLatest(changeEditModeState.type, changeEditModeStateSaga);
  yield takeLatest(
    [getReportingUserListData.type, incrementReportingUserPage.type, setReportingUserSearchExpression.type],
    getReportingUserListSaga,
  );
  yield takeLatest(
    [getCauseOfIncidentListData.type, setCauseOfIncidentSearchExpression.type],
    getCauseOfIncidentOptionsSaga,
  );
  yield takeLatest([getStateListData.type, setStateListSearchExpression.type], getStateListSaga);
  yield takeLatest([addEditedField.type, resetEditedField.type], validateFieldsSaga);
  yield takeLatest(saveEditModeData.type, saveEditModeDataSaga);
  yield takeEvery(getAssigneeById.type, getPendingAssigneesSaga);
  yield takeLatest([getAssignmentListData.type, setAssignmentListSearchExpression.type], getAssignmentListSaga);
}
